import stGeorgesGroup from "../assets/st-georges-group.webp";
import westSteps1 from "../assets/west-full-1.webp";
import westSteps2 from "../assets/west-full-2.webp";
import coachingPairsConvo from "../assets/coaching-pairs-convo.webp";

// Central image references
export const IMAGES = {
  stGeorges: stGeorgesGroup,
  westSteps1,
  westSteps2,
  coachingPairs: coachingPairsConvo,
  boardroomModern:
    "https://images.unsplash.com/photo-1517502884422-41eaead166d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  glass:
    "https://images.unsplash.com/photo-1658051894414-87a47e247e2a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  orangeAccent:
    "https://images.unsplash.com/photo-1586198502581-b26bf76810d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  heroBg:
    "https://images.pexels.com/photos/7648047/pexels-photo-7648047.jpeg?auto=compress&cs=tinysrgb&w=1600",
  portrait:
    "https://images.pexels.com/photos/5439380/pexels-photo-5439380.jpeg?auto=compress&cs=tinysrgb&w=1400",
  teamCoaching:
    "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1400",
  windsor: "https://images.pexels.com/photos/13020613/pexels-photo-13020613.jpeg",
  speaking:
    "https://images.pexels.com/photos/6703915/pexels-photo-6703915.jpeg?auto=compress&cs=tinysrgb&w=1400",
  talks:
    "https://images.pexels.com/photos/7433840/pexels-photo-7433840.jpeg?auto=compress&cs=tinysrgb&w=1600",
  boardroom:
    "https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?auto=compress&cs=tinysrgb&w=1400",
  handshake:
    "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1400",
  videoBg:
    "https://images.pexels.com/photos/7176319/pexels-photo-7176319.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

// The six elements of Coaching Pairs
export const SIX_ELEMENTS = [
  {
    n: "01",
    title: "Working with someone we trust",
    body: "Everything starts with trust. That's why each leader chooses their own coaching partner – assuming the best of one another from the very first conversation.",
    example: "A Finance Director picks the Head of Operations – someone they respect but rarely work with closely – and within one session they're speaking more openly than they ever have in a Board meeting.",
  },
  {
    n: "02",
    title: "Championing each other",
    body: "We rate our partner as a leader, and feel it reciprocated. A champion reminds us we already have a lot to offer, making honest feedback far easier to hear.",
    example: "Instead of 'here's what you're doing wrong', a partner opens with 'you're the best I've seen at rallying a room – so let's talk about the one thing holding that back.'",
  },
  {
    n: "03",
    title: "Setting one or two goals",
    body: "Together we identify the one or two behaviours that, if changed, would have the biggest impact on our leadership. Short lists beat shopping lists.",
    example: "A CEO commits to a single goal: stop jumping in with the answer in meetings, and leave ten seconds of silence for others to fill.",
  },
  {
    n: "04",
    title: "Inviting feedback from the team",
    body: "We take our chosen focus to a few trusted colleagues and ask, as equals, whether we've put our finger on the right things to work on.",
    example: "A director asks three colleagues, 'When am I at my most and least useful in a crisis?' – and discovers the pattern they'd completely missed.",
  },
  {
    n: "05",
    title: "Testing out new ways of leading",
    body: "Back in our pair, we plan practical experiments. In the business of improving as leaders, we don't do failure – the only question is how big the improvement.",
    example: "For a month, a leader delegates every decision that isn't truly theirs to make – then brings the results back to their pair to review together.",
  },
  {
    n: "06",
    title: "Sharing lessons & choosing anew",
    body: "Every six months the team reconvenes to share insights, then each leader pairs with someone new – building a culture where everyone grows everyone.",
    example: "At the six-month awayday, ten leaders each share one hard-won lesson – then swap partners, spreading trust across the whole top team.",
  },
];

// 6-month cycle steps
export const CYCLE_STEPS = [
  {
    title: "Pair up",
    body: "You pair with a colleague from another function in your extended leadership team – coaching partners, supporting each other as equals.",
  },
  {
    title: "Set your goals",
    body: "Each of you sets one or two development goals, consulting colleagues on what they could make possible for you.",
  },
  {
    title: "Coach monthly",
    body: "You meet for at least an hour a month in a confidential 'bubble of safe space', helping each other step up and let go of old habits.",
  },
  {
    title: "Reconvene & renew",
    body: "After six months the team comes together to share insights – then everyone chooses a new partner for the next round.",
  },
];

// Top team / 1-2-1 offerings
export const TOP_TEAM_SERVICES = [
  {
    title: "1-2-1 Executive Coaching",
    body: "A confidential partnership for CEOs and Directors – helping leaders who feel constrained overcome ingrained behaviours and redirect their energy towards impactful leadership.",
  },
  {
    title: "Team Coaching",
    body: "Transforming groups of talented individuals into cohesive, high-trust units through customised development programmes built around your team's real dynamics.",
  },
  {
    title: "Strategic Awaydays & Facilitation",
    body: "Facilitating Boards and Executive teams to achieve breakthroughs in their thinking – and the trust that makes genuine candour possible.",
  },
];

// Benefit bullets
export const ONE_TO_ONE_BENEFITS = [
  {
    title: "Personalised support",
    body: "Tailored sessions addressing your unique challenges and aspirations.",
  },
  {
    title: "Strategic clarity",
    body: "Gain fresh perspectives on complex issues and refine your strategic vision.",
  },
  {
    title: "Empowered leadership",
    body: "Develop the confidence and tools to lead with greater influence and effectiveness.",
  },
];

export const TEAM_BENEFITS = [
  {
    title: "Enhanced collaboration",
    body: "Bridge functional gaps and foster a culture of collective responsibility.",
  },
  {
    title: "Conflict resolution",
    body: "Address interpersonal challenges constructively, ensuring team cohesion.",
  },
  {
    title: "Strategic alignment",
    body: "Unify your team around shared goals and accelerate strategic execution.",
  },
];

// Coaching Pairs – how it works
export const PAIRS_HOW_IT_WORKS = [
  {
    title: "Strategic pairing",
    body: "Leaders are carefully paired with a peer from a different functional team, fostering new connections and perspectives.",
  },
  {
    title: "Mutual growth",
    body: "Each pair commits to a six-month cycle of reciprocal coaching, focusing on individual development goals.",
  },
  {
    title: "Continuous evolution",
    body: "After each cycle, leaders rotate to a new partner, continuously building relationships and expanding their influence.",
  },
];

export const PAIRS_IMPACT = [
  {
    title: "Builds bridges",
    body: "Transforms a 'them and us' culture into one of 'only us', strengthening cross-team collaboration.",
  },
  {
    title: "Accelerates development",
    body: "Leaders gain diverse insights and actively champion each other's growth.",
  },
  {
    title: "Sustainable culture",
    body: "Embeds a self-sustaining model of leader-leader coaching, making it standard practice for top teams.",
  },
];

// Why partner with Pete
export const WHY_PARTNER = [
  {
    title: "Unrivalled experience",
    body: "Decades of working with CEOs and top teams, including extensive work within the walls of Windsor Castle.",
  },
  {
    title: "Impact-driven",
    body: "Focused on delivering tangible results that enhance your leadership and organisational performance.",
  },
  {
    title: "Risk-free engagement",
    body: "A full money-back guarantee for any unused sessions – ensuring your complete satisfaction and control.",
  },
];

// Fees / process accordion
export const FEES = [
  {
    q: "How we begin",
    a: "I first offer a free 30-minute exploratory conversation online, to make sure we feel we'd be a good match before going any further.",
  },
  {
    q: "The coaching series",
    a: "If we decide to proceed, I ask you to commit to a series of five one-hour sessions. Most of my 1-2-1 work is now online via Teams or Zoom – and for many CEOs this proves even more effective than in person.",
  },
  {
    q: "Invoicing & notice",
    a: "I invoice up-front once we've agreed what suits you best, and ask for at least 24 hours' notice if you need to cancel or postpone a session.",
  },
  {
    q: "Full money-back guarantee",
    a: "If at any point the coaching isn't giving you the support you need, you can exit immediately and I refund any time we haven't yet used – no questions asked. No-one has ever triggered this, but to me it's an important principle: you keep control.",
  },
  {
    q: "Face-to-face & travel",
    a: "Some clients come to my home in Goring-on-Thames, South Oxfordshire, and sometimes I travel to them. When I travel, I charge travel time and costs at one-third of my hourly coaching fee.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Pete has a rare gift for asking the one question that unlocks everything. Our top team is more united than it has ever been.",
    name: "Chief Executive",
    role: "National Charity",
  },
  {
    quote:
      "The Coaching Pairs approach transformed how our directors relate to one another. It became self-sustaining within a year.",
    name: "Group HR Director",
    role: "Financial Services",
  },
  {
    quote:
      "Working with Pete one-to-one gave me the confidence to lead differently. Genuinely a turning point in my career.",
    name: "Managing Director",
    role: "Professional Services",
  },
];


// Video testimonials.
// To go live, set EITHER `youtubeId` (e.g. "dQw4w9WgXcQ"), a Vimeo `vimeoId`,
// or a direct `src` (mp4 URL / uploaded file path). Leave all null to show a
// tasteful "coming soon" state.
export const VIDEO_TESTIMONIALS = [
  {
    id: "vt1",
    name: "Sarah Walker-Smith",
    role: "Chief Executive, AMPA & Shakespeare Martineau",
    quote: "",
    poster: "/vt-sarah-2.jpg",
    youtubeId: null,
    vimeoId: null,
    src: "https://video.wixstatic.com/video/0ce061_840232c8e62b4d74a1f168cc49472ff0/720p/mp4/file.mp4",
    transcript:
      "Hi, I just want to talk about Pete Ashby. My goodness, so much to say about Pete Ashby. I've known Pete for the best part of two years now and it's been an absolute privilege to have him in my life. I can absolutely say he has made a difference to me. I first met him during some group leadership coaching sessions. I then had some individual sessions with him and some further online sessions as we've been in lockdown most of the last year. So I've seen him operate in lots of different ways. A couple of things I'd say about Pete: I've also worked with other coaches. If you want a coach who's going to ruthlessly set objectives, write up notes, meet with you methodically and go through those notes, challenge you to deliver those objectives, force you to write action plans and be very process driven – Pete is not the guy for you. If, however, you want a coach who is really going to get to know the whole you, to get past those layers, to get inside and really understand what makes you tick, to challenge you on the areas that you need to be challenged on, but utterly support you around the things where you need support, then I simply can't think of anybody better than Pete. He has an absolute way of getting to the heart of the matter. He holds you to account, but not in a way that's driven by process – in fact you won't even notice there's a process going on. He does it by instinct, by empathy, by really getting into the heart of a problem, and from having a huge amount of experience working with many leaders and professionals over the last couple of decades in similar positions to you. But he totally understands that you are you, and he will work with you in your way. I can't recommend Pete enough. He's made a massive difference to me and the groups of people that have worked with him – I've seen changes in all of us. So thanks for everything you do, Pete, and just keep being you.",
  },
  {
    id: "vt2",
    name: "Mark d'Inverno",
    role: "Professor & former Pro Vice-Chancellor, Goldsmiths, University of London",
    quote: "",
    poster: "/vt-mark-5.jpg",
    youtubeId: null,
    vimeoId: null,
    src: "https://video.wixstatic.com/video/0ce061_e096645b7a6b48d1b7bd2167aca3002b/1080p/mp4/file.mp4",
    transcript:
      "I'm going to speak about the truly remarkable Pete Ashby. My name is Mark d'Inverno. I'm an academic, a professor, and for many years have been in senior management in higher education. We first met when Pete was running the Leadership Fellow scheme at Windsor Castle, and I went on three or four of these intensive sessions with overnight stays, which looked at professional and personal development as applied to leadership. It covered a range of issues – authenticity, how we challenge ourselves, our responsibility to ourselves and others, courage and humility – quite complex, challenging conversations. I was always struck by how Pete found exactly the right way of bringing people together, so that all of the voices were heard and people could bring all of themselves into the conversation. Everybody who went on these sessions was truly touched by Pete; he had a huge impact. So I really wanted to work with Pete as my one-to-one coach, and I asked him, and immediately he had an impact. He helped me understand how I can take greater responsibility for my professional life and a greater sense of what is important to me, to the roles I'm in, and to thinking about what I could be in the future. He helped me develop the confidence to go for a chief executive position running a university in the UK, and supported me every step of the way in that application – and I was offered the position at the end of that process. He helped me understand what a profoundly creative and human act it is to be a leader: that sustained ability to connect with people, to always be thinking about ways we can challenge ourselves and each other to be the best we can be. He also helped me understand that leadership is as much about your vulnerabilities and what you think of as your weaknesses as your strengths – everything that makes you human can be put into that creative and social process of leadership. The only problem with Pete is that everything he says truly is extraordinary – an incredible insight into the way you think or what you're trying to achieve. So I'd strongly suggest, in this new online world, that you record the sessions. Try one session. I'm absolutely sure you will not be disappointed. Thank you for listening.",
  },
];

// The 10 Commitments – Tough Trust Method
export const COMMITMENTS = [
  { n: "01", title: "We assume the best – and verify the rest",
    body: "High trust remains our default operating system. But trust unsupported by checking is sentimentality, not leadership.",
    why: "Soft trust collapses the first time someone over-manages the truth. Tough trust survives it." },
  { n: "02", title: "Truth comes first, and it travels fastest",
    body: "No one in this organisation will be punished for naming what they see. Bad news must reach the Board before it reaches the regulator.",
    why: "Most failures of governance are failures of escalation. Tough Trust removes the friction." },
  { n: "03", title: "Whistleblowers are respected and championed",
    body: "A formal, protected, monitored channel – with a named Non-Executive sponsor. Retaliation is a dismissable act.",
    why: "The Failure to Prevent Fraud offence makes the absence of safe disclosure a Board-level liability." },
  { n: "04", title: "No secret zones",
    body: "Every division, joint venture and special project is in-scope for review. Closed rooms are where fraud composts.",
    why: "Silos can create their own secrets, and secrets often hide excuses. No secrets, no excuses." },
  { n: "05", title: "We run unscheduled deep-dives",
    body: "At any moment, any team – finance, ops, sales pipeline, supplier onboarding – may be selected for a transparency review.",
    why: "Randomness is the only defence against over-polished updates that gloss over hard truths." },
  { n: "06", title: "Wilful blindness is actively discouraged",
    body: "'I didn't want to know' is not a defence at this Board. Curiosity about uncomfortable signals is a leadership duty.",
    why: "The most expensive sentence in corporate history is 'I assumed someone else was on it'." },
  { n: "07", title: "Mavericks do not hijack the culture",
    body: "Disruption that breaks groundrules is not innovation – it is governance debt. We name it early.",
    why: "Soft trust cultures can easily be hijacked. Tough trust cultures warn off prospective hijackers through fear of exposure." },
  { n: "08", title: "We link high trust to high performance",
    body: "We learn publicly from the teams that combine candour and results - and delight in their success.",
    why: "Tough trust is not a control regime. It is the operating system of the most effective teams we work with." },
  { n: "09", title: "We practise what we preach",
    body: "Whenever the CEO has any bad news for the Board, it is delivered up-front and signalled in advance.",
    why: "Too many CEOs tell their Board what they want to hear. For us, bad news is better than false news any day." },
  { n: "10", title: "This Commitment evolves",
    body: "Every few months, we review what worked, what didn't, and what we now see differently. Changes are logged, dated and explained.",
    why: "A static commitment is a dead one. Tough trust earns its name by being rewritten in daylight." },
];
